/**
 * Loan calculation utility — single source of truth for the calculator and hero preview.
 * Consumer credit ("İstehlak krediti") — 36% annual annuity, approved rate.
 */

// Approved / verified inputs — not user-editable
export const DEFAULT_INTEREST_RATE = 36; // % annual (İstehlak krediti — approved)

// Product this simplified calculator produces
export const CALCULATOR_PRODUCT_SLUG = 'istehlak-kreditleri';
export const CALCULATOR_PRODUCT_LABEL = 'İstehlak Krediti';

// Range constraints — aligned to İstehlak Krediti (the only product the online form applies to)
export const AMOUNT_MIN = 500;
export const AMOUNT_MAX = 10000;
export const AMOUNT_STEP = 100;
export const TERM_MIN = 3;
export const TERM_MAX = 36;
export const TERM_STEP = 1;

// Default / example values (also used by hero preview)
export const HERO_EXAMPLE_AMOUNT = 5000;
export const HERO_EXAMPLE_TERM = 12;

/**
 * Annuity monthly payment — equal payments over the term.
 * Safe for edge cases (invalid inputs return 0).
 *
 * @param {number} principal - loan amount in AZN
 * @param {number} months - term in months
 * @param {number} annualRatePercent - annual interest rate as percent (e.g. 35 for 35%)
 * @returns {number} monthly payment in AZN
 */
export const calcMonthlyPayment = (principal, months, annualRatePercent) => {
  const p = Number(principal);
  const n = Number(months);
  const rate = Number(annualRatePercent);
  if (!Number.isFinite(p) || !Number.isFinite(n) || !Number.isFinite(rate)) return 0;
  if (p <= 0 || n <= 0) return 0;
  const r = rate / 100 / 12;
  if (r === 0) return p / n;
  const pow = Math.pow(1 + r, n);
  const denom = pow - 1;
  if (denom === 0) return 0;
  return (p * (r * pow)) / denom;
};

/**
 * Format an amount with 2 decimals and Azerbaijani-style separators.
 * Example: 425.5 → "425,50"; 1234.5 → "1 234,50"
 */
export const formatAzn = (v) => {
  if (!Number.isFinite(v)) return '0,00';
  const parts = Math.abs(v).toFixed(2).split('.');
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${intPart},${parts[1]}`;
};

/**
 * Format a whole-number amount with Azerbaijani-style thousands separator.
 * Example: 5000 → "5 000"; 50000 → "50 000"
 */
export const formatAmountInt = (v) => {
  if (!Number.isFinite(v)) return '0';
  return Math.round(Math.abs(v)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

/**
 * Clamp a numeric input into [min, max] range, safe for empty / NaN.
 */
export const clamp = (v, min, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
};
